import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { IUserSlice, TCart } from "@/redux/slices/user/types";
import { addToCart } from "@helpers/siteTracking/events";

type TCartDomainData = {
  action: 1 | 16 | 18;
  domain_name: string;
  tld: string;
  period: string;
  premium?: boolean;
};

type TCartNumberData = {
  country_code?: string;
  area_code?: string;
  number: string;
};

interface IPostCartItemProps {
  itemType: number;
  itemSubType: number;
  itemData: TCartDomainData | TCartNumberData;
}

interface IMetaObj {
  meta: {
    arg: IPostCartItemProps;
  };
}

export const postCartItem = createAsyncThunk(
  "user/postCartItem",
  async (
    { itemType, itemSubType, itemData }: IPostCartItemProps,
    { rejectWithValue },
  ) =>
    await axios({
      method: "POST",
      url: `/api/cart?version=2`,
      data: {
        item_type: itemType,
        item_sub_type: itemSubType,
        item_data: itemData,
      },
    })
      .catch((err) => {
        throw rejectWithValue(err.response.data.message);
      })
      .then((res: any) => {
        addToCart();
        return res.data;
      })
);

const pending = (state: IUserSlice, { meta: { arg } }: IMetaObj) => {
  const isNumber = (arg.itemData as any)?.number;

  state.cart = {
    ...state.cart,
    items: [
      ...state.cart.items,
      isNumber
        ? {
          cost_gross: 0,
          cost_net: 0,
          cost_recurring: 0,
          cost_service: 0,
          id: (arg.itemData as TCartNumberData).number,
          name: (arg.itemData as TCartNumberData).number,
          loading: true,
          is_recurring: false,
          period: 1,
          type: {
            id: 1,
            sub_type_id: 1,
            name: "Number",
            variation: 1,
            action: "create",
          },
          extra_details: {},
          requires_extra_details: false,
        }
        : {
          cost_gross: 0,
          cost_net: 0,
          cost_recurring: 0,
          cost_service: 0,
          id: (arg.itemData as TCartDomainData).domain_name,
          name: (arg.itemData as TCartDomainData).domain_name,
          loading: true,
          is_recurring: false,
          period: 1,
          type: {
            id: 1,
            sub_type_id: 1,
            name: "Domain Name",
            variation: 1,
            action: "create",
          },
          extra_details: {},
          requires_extra_details: false,
        },
    ],
  };
};

const fulfilled = (
  state: IUserSlice,
  { payload, meta }: { payload: { cart: TCart }; meta: IMetaObj["meta"] },
) => {
  state.cart = {
    ...payload.cart,
    items: state.cart.items
      .filter(
        (item) =>
          item.id !== (meta.arg.itemData as TCartNumberData).number &&
          item.id !== (meta.arg.itemData as TCartDomainData).domain_name
      )
      .concat(
        payload.cart.items.filter(
          (item) =>
            !state.cart.items.some((cartItem) => cartItem.id === item.id)
        )
      ),
  };
};

const rejected = (state: IUserSlice, { meta: { arg } }: IMetaObj) => {
  state.cart = {
    ...state.cart,
    items: state.cart.items.filter(
      (item) =>
        item.id !== (arg.itemData as TCartNumberData).number &&
        item.id !== (arg.itemData as TCartDomainData).domain_name
    ),
  };
};

export default {
  action: postCartItem,
  fulfilled,
  pending,
  rejected,
};
